import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePlayerLocation } from '../game-map/PlayerLocationContext';
import { DEFAULT_PLAYER_POSITION, haversineDistanceMeters } from '../game-map/core.mjs';
import { useGamification } from '../hooks/GamificationContext';
import { runtime } from '../utils/runtime';
import { actOnEncounter, completeEncounter, encounterRequestId, heartbeatEncounter, relocateEncounter, startEncounter } from '../api/encountersApi';
import { ENCOUNTER_COPY } from './copy';
import {
  ENCOUNTER_ACTION_GPS_REFRESH_TIMEOUT_MS,
  ENCOUNTER_DEBUG_GPS_GRACE_MS,
  encounterActionErrorMessage,
  encounterGpsFixState,
  encounterSpawnNotice,
  encounterTarget,
  normalizeEncounterDebugType,
  resolveEncounterVisualPosition,
  shouldSendEncounterHeartbeat,
} from './core.mjs';

const EncounterContext = createContext(null);

function debugTypeFromUrl() {
  return normalizeEncounterDebugType(new URLSearchParams(window.location.search).get('pfp_encounter_debug'));
}

export function EncounterProvider({ children }) {
  const player = usePlayerLocation();
  const gamification = useGamification();
  const [encounter, setEncounter] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [discoveryReason, setDiscoveryReason] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [spawnNotice, setSpawnNotice] = useState('');
  const [pending, setPending] = useState(false);
  const [gpsRefreshing, setGpsRefreshing] = useState(false);
  const [lastActionGps, setLastActionGps] = useState(null);
  const [serverRejection, setServerRejection] = useState(null);
  const [debugFallbackReady, setDebugFallbackReady] = useState(false);
  const lastHeartbeatRef = useRef(null);
  const heartbeatPendingRef = useRef(false);
  const encounterRef = useRef(null);
  const pendingRef = useRef(false);
  const debugType = runtime.randomEncountersDebugAllowed ? debugTypeFromUrl() : '';
  const enabled = Boolean(runtime.randomEncountersEnabled || debugType);
  encounterRef.current = encounter;

  useEffect(() => {
    setDebugFallbackReady(false);
    if (!debugType) return undefined;
    const timer = window.setTimeout(() => setDebugFallbackReady(true), ENCOUNTER_DEBUG_GPS_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, [debugType]);

  const debugFallbackPosition = debugFallbackReady ? { ...DEFAULT_PLAYER_POSITION, accuracy: 5, timestamp: Date.now(), source: 'debug' } : null;
  const visualPosition = resolveEncounterVisualPosition(player.position, debugType, debugFallbackReady, debugFallbackPosition);
  const target = encounterTarget(encounter);
  const distance = visualPosition && target ? haversineDistanceMeters(visualPosition, { latitude: target.lat, longitude: target.lng }) : Number.POSITIVE_INFINITY;

  const publishEncounter = useCallback((next, reason) => {
    if (!next) return;
    const previous = encounterRef.current;
    setEncounter(next);
    if (next.status === 'discovered' && previous?.id !== next.id) {
      setDiscoveryReason(reason || 'active_exists');
      setPopupOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !visualPosition || heartbeatPendingRef.current) return;
    const previous = lastHeartbeatRef.current;
    if (!shouldSendEncounterHeartbeat(previous, visualPosition, Date.now(), haversineDistanceMeters)) return;
    heartbeatPendingRef.current = true;
    lastHeartbeatRef.current = { position: visualPosition, sentAt: Date.now() };
    heartbeatEncounter(visualPosition, debugType)
      .then((payload) => {
        setError('');
        setSpawnNotice(encounterSpawnNotice(payload, debugType));
        if (payload?.encounter) publishEncounter(payload.encounter, payload.reason);
      })
      .catch(() => setError('Begegnungen können gerade nicht aktualisiert werden. Eine aktive Begegnung bleibt sichtbar.'))
      .finally(() => { heartbeatPendingRef.current = false; });
  }, [debugType, enabled, publishEncounter, visualPosition]);

  useEffect(() => {
    if (!encounter?.expiresAt) return undefined;
    const timer = window.setInterval(() => {
      if (Date.now() < Number(encounterRef.current?.expiresAt || 0)) return;
      setEncounter(null);
      setPopupOpen(false);
      setNotice('Die Begegnung ist weitergezogen.');
    }, 1000);
    return () => window.clearInterval(timer);
  }, [encounter?.expiresAt]);

  const captureActionGps = useCallback((fix) => {
    const gps = encounterGpsFixState(fix, Date.now());
    const actionDistance = fix && target
      ? haversineDistanceMeters(fix, { latitude: target.lat, longitude: target.lng })
      : Number.POSITIVE_INFINITY;
    setLastActionGps({
      state: gps.state,
      ageMs: gps.ageMs,
      accuracy: gps.accuracy,
      distance: Number.isFinite(actionDistance) ? actionDistance : null,
    });
    return fix;
  }, [target]);

  const actionPosition = useCallback(async () => {
    const current = player.getLatestVerifiedGpsFix();
    if (encounterGpsFixState(current, Date.now()).usable) return captureActionGps(current);
    setGpsRefreshing(true);
    try {
      const refreshed = await player.waitForFreshFix(ENCOUNTER_ACTION_GPS_REFRESH_TIMEOUT_MS);
      return captureActionGps(refreshed);
    } finally {
      setGpsRefreshing(false);
    }
  }, [captureActionGps, player]);

  const run = useCallback(async (task) => {
    if (pendingRef.current) return null;
    pendingRef.current = true;
    setPending(true);
    setError('');
    setServerRejection(null);
    try {
      return await task();
    } catch (actionError) {
      const payload = actionError?.payload || {};
      const rejectionData = payload?.data || actionError?.gpsState || {};
      setServerRejection({
        reason: String(rejectionData?.reason || payload?.code || actionError?.code || 'action_failed'),
        maxAccuracy: rejectionData?.max_accuracy ?? null,
        receivedAccuracy: rejectionData?.received_accuracy ?? rejectionData?.accuracy ?? null,
        maxAgeSeconds: rejectionData?.max_age_seconds ?? null,
        receivedAgeSeconds: rejectionData?.received_age_seconds ?? (Number.isFinite(Number(rejectionData?.ageMs)) ? Number(rejectionData.ageMs) / 1000 : null),
      });
      setError(encounterActionErrorMessage(actionError));
      return null;
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }, []);

  const start = useCallback(() => run(async () => {
    const position = await actionPosition();
    const next = await startEncounter(encounter.id, position);
    setEncounter(next);
    setPopupOpen(false);
    return next;
  }), [actionPosition, encounter?.id, run]);

  const dismiss = useCallback(() => run(async () => {
    const position = await actionPosition();
    await actOnEncounter(encounter.id, 'dismiss', position, encounterRequestId('dismiss'));
    setEncounter(null);
    setPopupOpen(false);
    setNotice('Du ziehst weiter.');
  }), [actionPosition, encounter?.id, run]);

  const relocate = useCallback(() => run(async () => {
    const position = await actionPosition();
    const next = await relocateEncounter(encounter.id, position, encounterRequestId('relocate'));
    setEncounter(next);
    setPopupOpen(false);
    setNotice('Der Begegnungsort wurde sicher neu gewählt.');
    return next;
  }), [actionPosition, encounter?.id, run]);

  const finish = useCallback(() => run(async () => {
    const position = await actionPosition();
    const next = await completeEncounter(encounter.id, position, encounterRequestId('complete'));
    setEncounter(next);
    gamification?.applyGamificationPayload?.(next?.gamification || {});
    setNotice(ENCOUNTER_COPY[next.type]?.success || 'Begegnung gemeistert!');
    window.setTimeout(() => {
      if (encounterRef.current?.id === next.id && encounterRef.current?.status === 'completed') setEncounter(null);
    }, 1400);
    return next;
  }), [actionPosition, encounter?.id, gamification, run]);

  const act = useCallback((action) => run(async () => {
    const position = await actionPosition();
    const next = await actOnEncounter(encounter.id, action, position, encounterRequestId(action));
    setEncounter(next);
    if (next?.action_result === 'missed') setNotice(ENCOUNTER_COPY.verlorene_buddel.fail);
    if (next?.action_result === 'caught') {
      setNotice('Treffer! Die Buddel ist im Fangnetz.');
      const completed = await completeEncounter(encounter.id, position, encounterRequestId('complete'));
      setEncounter(completed);
      gamification?.applyGamificationPayload?.(completed?.gamification || {});
      setNotice(ENCOUNTER_COPY.verlorene_buddel.success);
      window.setTimeout(() => {
        if (encounterRef.current?.id === completed.id && encounterRef.current?.status === 'completed') setEncounter(null);
      }, 1400);
      return completed;
    }
    return next;
  }), [actionPosition, encounter?.id, gamification, run]);

  const value = useMemo(() => ({
    enabled,
    encounter,
    popupOpen,
    discoveryReason,
    setPopupOpen,
    notice,
    clearNotice: () => { setNotice(''); setError(''); setSpawnNotice(''); },
    spawnNotice,
    error,
    pending,
    gpsRefreshing,
    latestGpsFix: player.position,
    lastActionGps,
    serverRejection,
    playerPosition: visualPosition,
    distance,
    start,
    dismiss,
    relocate,
    act,
    finish,
  }), [act, dismiss, discoveryReason, distance, enabled, encounter, error, finish, gpsRefreshing, lastActionGps, notice, pending, player.position, popupOpen, relocate, serverRejection, spawnNotice, start, visualPosition]);

  return <EncounterContext.Provider value={value}>{children}</EncounterContext.Provider>;
}

export function useEncounter() {
  const context = useContext(EncounterContext);
  if (!context) throw new Error('useEncounter benötigt den EncounterProvider.');
  return context;
}
